import { signOut } from '@backoffice-frontend/authentication'
import { LOGIN_INTENDED_PATH } from '@backoffice-frontend/common'
import { MoiaLogo } from '@backoffice-frontend/patterns'
import { Button, Callout, Card, Text } from '@moia-dev/pace-web'
import { useTranslation } from 'react-i18next'
import store from 'store'

type WrongSubdomainViewProps = {
  expectedUrl?: string
}

export const WrongSubdomainView = ({
  expectedUrl,
}: WrongSubdomainViewProps) => {
  const { t } = useTranslation()

  // Sign out must never depend on resolving the URL, otherwise a failed lookup
  // leaves the user on a screen with no way out.
  const handleSignOut = async () => {
    store.remove(LOGIN_INTENDED_PATH)
    await signOut()
  }

  // Tokens live per origin, so the correct Backoffice asks for a sign-in again.
  // Dropping the intended path keeps the sign-in on this origin from sending
  // them back to a page they may not be allowed on.
  const handleGoToCorrectUrl = () => {
    if (!expectedUrl) {
      return
    }
    store.remove(LOGIN_INTENDED_PATH)
    window.location.assign(expectedUrl)
  }

  return (
    <>
      <title>{t('authenticatedAppView_wrongSubdomain_title')}</title>
      <div
        css={{
          minHeight: '100vh',
          backgroundSize: 'cover',
          alignItems: 'flex-start',
          justifyItems: 'center',
          backgroundImage: 'url(/assets/images/login.png)',
          display: 'grid',
        }}
      >
        <MoiaLogo
          css={theme => ({
            width: '160px',
            marginTop: theme.space.Space6,
            color: theme.palette.common.white,
          })}
        />
        <Card
          css={theme => ({
            padding: theme.space.Space3,
            width: '400px',
            display: 'flex',
            flexDirection: 'column',
            gap: theme.space.Space2,
          })}
          outline
        >
          <Callout variant="warning" hideDismiss>
            <div
              css={theme => ({
                display: 'flex',
                flexDirection: 'column',
                gap: theme.space.Space2,
              })}
            >
              <Text>
                {expectedUrl
                  ? t('authenticatedAppView_wrongSubdomain_messageWithUrl')
                  : t('authenticatedAppView_wrongSubdomain_message')}
              </Text>
              {expectedUrl && (
                <div
                  css={theme => ({
                    display: 'flex',
                    flexDirection: 'column',
                    gap: theme.space.Space1,
                  })}
                >
                  <Text size="sm">
                    {t('authenticatedAppView_wrongSubdomain_correctUrlLabel')}
                  </Text>
                  <Text>{expectedUrl}</Text>
                </div>
              )}
            </div>
          </Callout>
          <div
            css={theme => ({
              display: 'flex',
              justifyContent: 'flex-end',
              gap: theme.space.Space2,
            })}
          >
            <Button
              variant={expectedUrl ? 'secondary' : 'primary'}
              onClick={handleSignOut}
              label={t('authenticatedAppView_wrongSubdomain_signOutButton')}
            />
            {expectedUrl && (
              <Button
                onClick={handleGoToCorrectUrl}
                label={t('authenticatedAppView_wrongSubdomain_goThereButton')}
              />
            )}
          </div>
        </Card>
      </div>
    </>
  )
}
